import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Link } from "react-router"
import { INDEX_PATH } from "./api"
import { operations } from "./operationsApi"

export function parseCompanies(text: string) {
  const named = text.includes("\t")
  const rows = text
    .trim()
    .split(named ? /\r?\n/ : /[,\r\n]+/)
    .map((row) => row.trim())
    .filter(Boolean)
  if (!rows.length || rows.length > 100) throw new Error("Invalid row count")
  return rows.map((row) => {
    const cells = named
      ? row.split("\t").map((value) => value.trim())
      : ["", row]
    if (
      cells.length !== 2 ||
      (named && !cells[0]) ||
      cells[0].length > 200 ||
      cells[1].length > 4096
    )
      throw new Error("Invalid columns")
    const raw = cells[1]
    const url = new URL(raw.includes("://") ? raw : `https://${raw}`)
    if (
      !["http:", "https:"].includes(url.protocol) ||
      !url.hostname.includes(".") ||
      /\s/.test(raw) ||
      url.username ||
      url.password
    )
      throw new Error("Use a public website URL or domain")
    return {
      name: named ? cells[0] : url.hostname.replace(/^www\./, ""),
      website: named && raw.includes("://") ? raw : url.href,
    }
  })
}

export function BatchCompanies() {
  const { user, hasPermission } = useUser()
  const { translate: t } = useTranslation()
  const cache = useQueryClient()
  const [text, setText] = useState("")
  const [rows, setRows] = useState<ReturnType<typeof parseCompanies> | null>(
    null,
  )
  const [queued, setQueued] = useState<Record<string, boolean>>({})
  const [invalid, setInvalid] = useState(false)
  const save = useMutation({
    mutationFn: () => operations.batch(rows!),
    onSuccess: (data) => {
      void cache.invalidateQueries({
        queryKey: ["company-index", user?.organization],
      })
      const createdIds = data.items
        .filter((item) => item.created)
        .map((item) => item.company.id)
      if (createdIds.length) queue.mutate(createdIds)
    },
  })
  const queue = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of new Set(ids)) {
        if (queued[id]) continue
        try {
          await operations.enqueue(id)
          setQueued((old) => ({ ...old, [id]: true }))
        } catch {
          setQueued((old) => ({ ...old, [id]: false }))
        }
      }
      void cache.invalidateQueries({
        queryKey: ["company-index", user?.organization, "operations"],
      })
    },
  })
  if (!hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD)) return null
  return (
    <section className="space-y-3 rounded-xl border p-5">
      <h2 className="font-semibold">{t("CI_BATCH_TITLE")}</h2>
      <p className="text-sm text-muted-foreground">{t("CI_BATCH_HINT")}</p>
      <Textarea
        aria-label={t("CI_BATCH_INPUT")}
        value={text}
        disabled={save.isPending || queue.isPending}
        maxLength={430000}
        onChange={(e) => {
          setText(e.target.value)
          setRows(null)
          setInvalid(false)
          setQueued({})
          save.reset()
        }}
      />
      <Button
        variant="outline"
        disabled={!text.trim() || save.isPending || queue.isPending}
        onClick={() => {
          try {
            setRows(parseCompanies(text))
            setInvalid(false)
            save.reset()
          } catch {
            setInvalid(true)
            setRows(null)
          }
        }}
      >
        {t("CI_BATCH_PREVIEW")}
      </Button>
      {invalid && <p role="alert">{t("CI_BATCH_INVALID")}</p>}
      {rows && !save.isSuccess && (
        <>
          <div className="max-h-64 overflow-auto rounded border">
            <table className="w-full text-left text-sm">
              <thead>
                <tr>
                  <th className="p-2">{t("CI_NAME")}</th>
                  <th className="p-2">{t("CI_WEBSITE")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i}>
                    <td className="p-2">{row.name}</td>
                    <td className="p-2 break-all">{row.website}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button
            disabled={save.isPending || queue.isPending}
            onClick={() => save.mutate()}
          >
            {t("CI_BATCH_SAVE")}
          </Button>
        </>
      )}
      {save.isError && <p role="alert">{t("CI_ERROR")}</p>}
      {save.data && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            {t("CI_BATCH_QUEUE_HINT")}
          </p>
          <Button
            disabled={
              queue.isPending ||
              save.data.items.every((item) => queued[item.company.id])
            }
            onClick={() =>
              queue.mutate(
                save.data.items
                  .filter((item) => !queued[item.company.id])
                  .map((item) => item.company.id),
              )
            }
          >
            {t("CI_BATCH_QUEUE")}
          </Button>
        </div>
      )}
      {save.data && (
        <ul className="space-y-2">
          {save.data.items.map((row, i) => (
            <li key={i}>
              <Link
                className="underline"
                to={`${INDEX_PATH}/${row.company.id}`}
              >
                {row.company.name}
              </Link>{" "}
              · {t(row.created ? "CI_COMPANY_CREATED" : "CI_COMPANY_REUSED")}
              {queued[row.company.id] !== undefined && (
                <span>
                  {" "}
                  ·{" "}
                  {t(
                    queued[row.company.id]
                      ? "CI_JOB_WAITING"
                      : "CI_QUEUE_FAILED",
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
